
import { useRef, useEffect, useCallback } from 'react';
import { Game } from '@/types/game';
import { useIsMobile } from '@/hooks/use-mobile';

export const useGameSession = (game: Game | undefined, isPlaying: boolean) => {
  const sessionStartTime = useRef<number | null>(null);
  const isMobile = useIsMobile();

const trackSession = useCallback(async () => {
    if (sessionStartTime.current && game?.id) {
        const durationSeconds = Math.round((Date.now() - sessionStartTime.current) / 1000);
        console.log(`[GameDetail] Tracking session for ${game.title}, duration: ${durationSeconds}s`);
        
        sessionStartTime.current = null; 

        if (durationSeconds > 5) { 
            // Get country from geolocation API
            let countryCode = null;
            try {
              const geoResponse = await fetch('https://ipapi.co/json/');
              if (geoResponse.ok) {
                const geoData = await geoResponse.json();
                countryCode = geoData.country_code;
              }
            } catch (error) {
              console.log('[GameDetail] Could not get country data:', error);
            }
            
            const payload = {
                gameId: game.id,
                durationSeconds: durationSeconds,
                deviceType: isMobile ? 'mobile' : 'desktop',
                countryCode: countryCode
            };
            
            try {
              const supabaseUrl = "https://owlyalitoeiisdeyknne.supabase.co";
              const anonKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im93bHlhbGl0b2VpaXNkZXlrbm5lIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDkzOTAyMzgsImV4cCI6MjA2NDk2NjIzOH0.9LxBVYsqn61yD2C_Q_6U4_iVUJrDOw-yLFHvNOz-peA";
              const endpoint = `${supabaseUrl}/functions/v1/track-game-session`;
              
              fetch(endpoint, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'apikey': anonKey
                },
                body: JSON.stringify(payload),
                keepalive: true,
              });
              console.log('[GameDetail] Session tracking request sent via fetch with keepalive.');

            } catch (error) {
              console.error('[GameDetail] Error tracking game session:', error);
            }
        } else {
          console.log('[GameDetail] Session too short, not tracking.');
        }
    }
  }, [game?.id, game?.title, isMobile]);

  useEffect(() => {
    if (isPlaying) {
      if (!sessionStartTime.current) {
        sessionStartTime.current = Date.now();
        console.log(`[GameDetail] Session started for ${game?.title}`);
      }
    } else {
      if (sessionStartTime.current) {
        trackSession();
      }
    }
  }, [isPlaying, game?.title, trackSession]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (sessionStartTime.current) {
        trackSession();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (sessionStartTime.current) {
        trackSession();
      }
    };
  }, [trackSession]);
};
